import React from "react";
import { useTranslation } from "react-i18next";
import { formatLength } from "./format";

const CORNERS = ["A", "B", "C", "D"];

/**
 * Floor plan of one room from the computed corner points, to scale, with each wall labelled.
 * Free shapes number their walls in the order they were entered, so the list and the sketch
 * can be matched; an outline that does not close shows the gap in red.
 */
const RoomSketch = ({ room, summary }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const points = summary?.points || [];
  if (points.length < 3) {
    return (
      <div className="ms-sketch ms-sketch--empty">{t("Enter the dimensions to see the plan.")}</div>
    );
  }

  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(...xs) - minX || 1;
  const height = Math.max(...ys) - minY || 1;
  const size = Math.max(width, height);
  const font = size / 16;
  // Labels of side walls stand beside them, so leave more room left and right.
  const padX = size * 0.32;
  const padY = size * 0.14;
  const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
  const cy = ys.reduce((a, b) => a + b, 0) / ys.length;

  const polygonClosed = room.shape !== "polygon" || (summary.closingGap ?? 0) < 1;
  const edges = points.map((p, i) => [p, points[(i + 1) % points.length], i]);
  const drawnEdges = polygonClosed ? edges : edges.slice(0, -1);

  // Label just outside the middle of an edge, anchored away from the wall.
  const labelAt = (a, b) => {
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    let nx = (b[1] - a[1]) / len;
    let ny = -(b[0] - a[0]) / len;
    if ((mx - cx) * nx + (my - cy) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    const anchor = nx > 0.5 ? "start" : nx < -0.5 ? "end" : "middle";
    return [mx + nx * font * 0.6, my + ny * font * 0.9, anchor];
  };

  const edgeLabel = index => {
    if (room.shape === "rectangle") return formatLength(index % 2 ? room.width : room.length, lng);
    if (room.shape === "quad") return formatLength(room.sides[index], lng);
    const walls = room.walls.filter(wall => wall.length > 0);
    return walls[index] ? `${index + 1}: ${formatLength(walls[index].length, lng)}` : "";
  };

  return (
    <svg
      className="ms-sketch"
      viewBox={`${minX - padX} ${minY - padY} ${width + 2 * padX} ${height + 2 * padY}`}
      role="img"
      aria-label={t("Floor plan")}
    >
      <polygon className="ms-sketch-floor" points={points.map(p => p.join(",")).join(" ")} />
      {drawnEdges.map(([a, b, i]) => (
        <line
          key={`e${i}`}
          className="ms-sketch-wall"
          x1={a[0]}
          y1={a[1]}
          x2={b[0]}
          y2={b[1]}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {!polygonClosed && (
        <line
          className="ms-sketch-gap"
          x1={points[points.length - 1][0]}
          y1={points[points.length - 1][1]}
          x2={points[0][0]}
          y2={points[0][1]}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {room.shape === "quad" && (
        <line
          className="ms-sketch-diagonal"
          x1={points[0][0]}
          y1={points[0][1]}
          x2={points[2][0]}
          y2={points[2][1]}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {(room.shape === "rectangle" ? drawnEdges.slice(0, 2) : drawnEdges).map(([a, b, i]) => {
        const [x, y, anchor] = labelAt(a, b);
        return (
          <text
            key={`l${i}`}
            className="ms-sketch-label"
            x={x}
            y={y}
            fontSize={font}
            textAnchor={anchor}
            dominantBaseline="middle"
          >
            {edgeLabel(i)}
          </text>
        );
      })}
      {room.shape === "quad" &&
        points.map((p, i) => (
          <text
            key={`c${i}`}
            className="ms-sketch-corner"
            x={p[0] + (p[0] < cx ? -font : font) * 0.7}
            y={p[1] + (p[1] < cy ? -font : font) * 0.7}
            fontSize={font}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {CORNERS[i]}
          </text>
        ))}
    </svg>
  );
};

export default RoomSketch;
